import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DesktopLayout from '../../../layout/DesktopLayout';
import BottomNav from '../../../BottomNav';
import { useDesktopTopBar } from '../../../../context/DesktopLayoutContext';
import { useBranch } from '../../../../context/BranchContext';
import { useIsDesktop } from '../../../reports/reportTokens';
import { useMediaLibraryStore } from '../../../../redux/display/mediaLibrary/mediaLibraryHooks';
import { usePlaylistStore } from '../../../../redux/display/playlist/playlistHooks';
import { useDisplayToastStore } from '../../../../redux/display/display/displayHooks';
import { buildDisplayMediaSrc } from '../../../../services/displayApi';
import { Icon } from '../../components/ui/Icon/Icon';
import { DisplayToastContainer } from '../../components/ui/DisplayToastContainer/DisplayToastContainer';
import { DeleteConfirmationModal, deleteConfirmTitle } from '../../components/ui/DeleteConfirmationModal/DeleteConfirmationModal';
import { MediaCard } from '../../components/data/MediaCard/MediaCard';
import MediaUploadArea from '../../components/forms/MediaUploadArea/MediaUploadArea';
import SearchInput from '../../components/forms/SearchInput/SearchInput';
import { displayPaths } from '../../paths';
import {
  MEDIA_LIBRARY_SLOT_LIMIT,
  type DisplayOrientation, type MediaKind, type MediaLibraryItem, type Playlist,
} from '../../data/types';
import { MediaCarouselPreviewModal, type PreviewMediaItem } from '../../components/ui/MediaCarouselPreviewModal/MediaCarouselPreviewModal';
import { OrientationIcon } from '../../components/ui/OrientationIcon/OrientationIcon';
import './MediaLibraryPage.css';

const BORDER = 'var(--card-border, #E2E5EC)';
const T1 = 'var(--t1, #1A1D2E)';
const T2 = 'var(--t2, #475569)';
const T3 = 'var(--t3, #9CA3B8)';
const ACCENT = 'var(--accent, #D23A3B)';
const FONT = 'var(--font-primary, DM Sans, sans-serif)';

type TypeFilter = 'all' | MediaKind;
type LibraryTab = 'upload' | 'playlists';

function StatCard({ icon, label, value, tone = T2 }: { icon: string; label: string; value: string | number; tone?: string }) {
  return (
    <div style={{ flex: '1 1 160px', minWidth: 150, background: 'var(--surface, #fff)', border: `1px solid ${BORDER}`, borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: 'var(--card-shadow, 0 1px 4px rgba(0,0,0,0.05))' }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: `${tone}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={icon} size={20} color={tone} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: T1, fontFamily: FONT, lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: 11, color: T3, fontFamily: FONT }}>{label}</div>
      </div>
    </div>
  );
}

function TabBar({ value, onChange }: { value: LibraryTab; onChange: (v: LibraryTab) => void }) {
  const tabs: { key: LibraryTab; label: string; icon: string }[] = [
    { key: 'upload', label: 'Upload Media', icon: 'perm_media' },
    { key: 'playlists', label: 'Playlists', icon: 'playlist_play' },
  ];
  return (
    <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${BORDER}` }}>
      {tabs.map((tab) => {
        const active = value === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'none', cursor: 'pointer',
              padding: '10px 16px', fontSize: 13, fontWeight: 600, fontFamily: FONT,
              color: active ? ACCENT : T3, borderBottom: `2px solid ${active ? ACCENT : 'transparent'}`, marginBottom: -1,
            }}
          >
            <Icon name={tab.icon} size={16} color={active ? ACCENT : T3} />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function TypeFilterTabs({ value, onChange, counts }: { value: TypeFilter; onChange: (v: TypeFilter) => void; counts: Record<TypeFilter, number> }) {
  const tabs: { key: TypeFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'image', label: 'Images' },
    { key: 'video', label: 'Videos' },
  ];
  return (
    <div style={{ display: 'flex', gap: 6, background: 'var(--hover, #F0F1F5)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: 4, flexShrink: 0 }}>
      {tabs.map((tab) => {
        const active = value === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer',
              padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: FONT,
              background: active ? ACCENT : 'transparent', color: active ? '#fff' : T2,
              transition: 'all .15s',
            }}
          >
            {tab.label}
            <span style={{ fontSize: 10, opacity: 0.85 }}>{counts[tab.key]}</span>
          </button>
        );
      })}
    </div>
  );
}

type PlaylistOrientationFilter = 'all' | DisplayOrientation;

/** Same grouped-segment visual as TypeFilterTabs, filters the playlist list by orientation. */
function PlaylistOrientationFilterTabs({
  value, onChange, counts,
}: { value: PlaylistOrientationFilter; onChange: (v: PlaylistOrientationFilter) => void; counts: Record<PlaylistOrientationFilter, number> }) {
  const tabs: { key: PlaylistOrientationFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'Portrait', label: 'Portrait' },
    { key: 'Landscape', label: 'Landscape' },
  ];
  return (
    <div style={{ display: 'flex', gap: 6, background: 'var(--hover, #F0F1F5)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: 4, flexShrink: 0 }}>
      {tabs.map((tab) => {
        const active = value === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer',
              padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: FONT,
              background: active ? ACCENT : 'transparent', color: active ? '#fff' : T2,
              transition: 'all .15s',
            }}
          >
            {tab.label}
            <span style={{ fontSize: 10, opacity: 0.85 }}>{counts[tab.key]}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Same grouped-segment visual as TypeFilterTabs (All/Images/Videos), reused for orientation pickers. */
function OrientationToggle({ value, onChange }: { value: DisplayOrientation; onChange: (v: DisplayOrientation) => void }) {
  const options: DisplayOrientation[] = ['Portrait', 'Landscape'];
  return (
    <div style={{ display: 'flex', gap: 6, background: 'var(--hover, #F0F1F5)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: 4, flexShrink: 0 }}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer',
              padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: FONT,
              background: active ? ACCENT : 'transparent', color: active ? '#fff' : T2,
              transition: 'all .15s',
            }}
          >
            <OrientationIcon orientation={opt} size={14} color={active ? '#fff' : T2} />
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function OrientationTag({ orientation }: { orientation: DisplayOrientation }) {
  const isPortrait = orientation === 'Portrait';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6,
      background: isPortrait ? '#EEF2FF' : '#ECFDF5',
      color: isPortrait ? '#4F46E5' : '#059669',
      border: `1px solid ${isPortrait ? '#C7D2FE' : '#A7F3D0'}`,
      fontSize: 10, fontWeight: 700, fontFamily: FONT,
    }}>
      <OrientationIcon orientation={orientation} size={12} color={isPortrait ? '#4F46E5' : '#059669'} />
      {orientation}
    </span>
  );
}

function MediaLibraryEmptyState({
  hasFilters, onAction, showAction = true,
  icon = 'perm_media', title = 'No media uploaded yet', subtitle = 'Upload your first image or video to build your library',
  actionIcon = 'upload_file', actionLabel = 'Upload Media',
}: {
  hasFilters: boolean;
  onAction?: () => void;
  /** Set false when the action would be redundant with UI already visible above the empty state (e.g. the upload area). */
  showAction?: boolean;
  icon?: string;
  title?: string;
  subtitle?: string;
  actionIcon?: string;
  actionLabel?: string;
}) {
  return (
    <div style={{ textAlign: 'center', padding: 56, color: T3 }}>
      <Icon name={hasFilters ? 'search_off' : icon} size={44} color={T3} style={{ display: 'block', margin: '0 auto 12px' }} />
      <div style={{ fontSize: 15, fontWeight: 700, color: T1, marginBottom: 4, fontFamily: FONT }}>
        {hasFilters ? 'No results found' : title}
      </div>
      <div style={{ fontSize: 13, color: T3, marginBottom: 16, fontFamily: FONT }}>
        {hasFilters ? 'Try adjusting your search or filter' : subtitle}
      </div>
      {!hasFilters && showAction && onAction && (
        <button
          onClick={onAction}
          style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 auto', padding: '9px 18px', borderRadius: 10, border: 'none', background: ACCENT, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}
        >
          <Icon name={actionIcon} size={16} color="#fff" />
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/** Thumbnail strip resolving the playlist's first few media entries against the library.
 *  Always uses the same aspect ratio regardless of playlist orientation, so Landscape and
 *  Portrait playlist cards stay the same height and align cleanly in the grid — individual
 *  thumbnails just `cover`-crop into that fixed frame either way. */
function PlaylistThumbStrip({ playlist, mediaItems }: { playlist: Playlist; mediaItems: MediaLibraryItem[] }) {
  const resolved = useMemo(
    () => [...playlist.mediaEntries]
      .sort((a, b) => a.sequenceNo - b.sequenceNo)
      .map((entry) => mediaItems.find((m) => m.id === entry.mediaId))
      .filter((m): m is MediaLibraryItem => Boolean(m)),
    [playlist.mediaEntries, mediaItems],
  );
  const visible = resolved.slice(0, 2);
  const overflow = resolved.length > 2 ? resolved.length - 1 : 0;

  if (visible.length === 0) {
    return (
      <div style={{
        aspectRatio: '16 / 8', borderRadius: 10, background: 'var(--hover, #F0F1F5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${BORDER}`,
      }}>
        <Icon name="perm_media" size={22} color={T3} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 6, aspectRatio: '16 / 8' }}>
      {visible.map((item, i) => (
        <div key={item.id} style={{ position: 'relative', flex: 1, borderRadius: 10, overflow: 'hidden', background: 'linear-gradient(180deg, #172033 0%, #101827 100%)' }}>
          {item.kind === 'video' ? (
            <video src={buildDisplayMediaSrc(item.id, item.mimeType) ?? item.url} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <img src={buildDisplayMediaSrc(item.id, item.mimeType) ?? item.url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
          {i === visible.length - 1 && overflow > 0 && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: '#fff', fontFamily: FONT }}>+{overflow}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PlaylistCard({
  playlist, mediaItems, mediaCountLabel, onPreview, onEdit, onDelete,
}: { playlist: Playlist; mediaItems: MediaLibraryItem[]; mediaCountLabel: string; onPreview?: () => void; onEdit: () => void; onDelete: () => void }) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [editHovered, setEditHovered] = useState(false);
  const [deleteHovered, setDeleteHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'var(--surface, #fff)', border: `1px solid ${hovered ? T3 : BORDER}`, borderRadius: 14, overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        boxShadow: hovered ? '0 10px 24px rgba(17,24,39,0.12)' : 'var(--card-shadow, 0 1px 4px rgba(0,0,0,0.05))',
        transform: hovered ? 'translateY(-2px)' : 'none',
        transition: 'box-shadow .2s ease, border-color .2s ease, transform .2s ease',
      }}
    >
      <div onClick={onPreview} style={{ padding: '12px 12px 0', cursor: onPreview ? 'pointer' : 'default' }}>
        <PlaylistThumbStrip playlist={playlist} mediaItems={mediaItems} />
      </div>

      <div onClick={onPreview} style={{ padding: '12px 14px 0', display: 'flex', flexDirection: 'column', gap: 8, cursor: onPreview ? 'pointer' : 'default' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: `${ACCENT}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name="playlist_play" size={16} color={ACCENT} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T1, fontFamily: FONT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={playlist.name}>
              {playlist.name}
            </div>
          </div>
          <OrientationTag orientation={playlist.orientation} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 2 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500, color: T2, fontFamily: FONT }}>
            <Icon name="folder" size={14} color={T3} />
            {mediaCountLabel}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: T3, fontFamily: FONT }}>
            <Icon name="calendar_today" size={12} color={T3} />
            {new Date(playlist.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
      </div>

      <div style={{ margin: '12px 14px 0', borderTop: `1px solid ${BORDER}` }} />
      <div style={{ padding: '8px 8px 8px 14px', display: 'flex', justifyContent: 'space-between' }}>
        <button
          type="button"
          onClick={onEdit}
          onMouseEnter={() => setEditHovered(true)}
          onMouseLeave={() => setEditHovered(false)}
          style={{
            display: 'flex', alignItems: 'center', gap: 4, border: 'none',
            padding: '5px 9px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 600,
            color: T2, fontFamily: FONT,
            background: editHovered ? 'var(--hover, #F0F1F5)' : 'none',
            transition: 'background .15s ease',
          }}
        >
          <Icon name="edit" size={14} color={T2} />
          Edit
        </button>
        <button
          type="button"
          onClick={() => setConfirmingDelete(true)}
          onMouseEnter={() => setDeleteHovered(true)}
          onMouseLeave={() => setDeleteHovered(false)}
          style={{
            display: 'flex', alignItems: 'center', gap: 4, border: 'none',
            padding: '5px 9px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 600,
            color: 'var(--red, #DC2626)', fontFamily: FONT,
            background: deleteHovered ? 'var(--red-soft, rgba(220,38,38,0.1))' : 'none',
            transition: 'background .15s ease',
          }}
        >
          <Icon name="delete" size={14} color="var(--red, #DC2626)" />
          Delete playlist
        </button>
      </div>

      <DeleteConfirmationModal
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={() => { setConfirmingDelete(false); onDelete(); }}
        title={deleteConfirmTitle(playlist.name)}
        description="This playlist will be permanently deleted. Displays currently using it will need a new assignment. This action cannot be undone."
      />
    </div>
  );
}

export default function MediaLibraryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;
  const setTopBarProps = useDesktopTopBar();
  const { selectedBranch } = useBranch();
  const { items, fetchMedia, uploadMedia, deleteMedia } = useMediaLibraryStore();
  const { items: playlists, fetchPlaylists, deletePlaylist } = usePlaylistStore();
  const { addToast } = useDisplayToastStore();

  const currentLocationId = selectedBranch?.id ?? (selectedBranch as any)?.locationId ?? '';

  const [tab, setTab] = useState<LibraryTab>(searchParams.get('tab') === 'playlists' ? 'playlists' : 'upload');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [uploadingFiles, setUploadingFiles] = useState<{ id: string; name: string; progress: number }[]>([]);
  const [uploadOrientation, setUploadOrientation] = useState<DisplayOrientation>('Portrait');
  const [playlistSearch, setPlaylistSearch] = useState('');
  const [playlistOrientationFilter, setPlaylistOrientationFilter] = useState<PlaylistOrientationFilter>('all');
  const [previewItem, setPreviewItem] = useState<MediaLibraryItem | null>(null);
  const [previewPlaylist, setPreviewPlaylist] = useState<Playlist | null>(null);

  useEffect(() => { setTopBarProps({ title: 'Media Library', breadcrumbs: 'Display' }); }, [setTopBarProps]);
  useEffect(() => { if (currentLocationId) { fetchMedia(currentLocationId); fetchPlaylists(currentLocationId); } }, [fetchMedia, fetchPlaylists, currentLocationId]);

  const counts = useMemo(() => {
    const image = items.filter((i) => i.kind === 'image').length;
    const video = items.filter((i) => i.kind === 'video').length;
    return { all: items.length, image, video } as Record<TypeFilter, number>;
  }, [items]);

  const remainingSlots = Math.max(0, MEDIA_LIBRARY_SLOT_LIMIT - items.length);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (typeFilter !== 'all' && item.kind !== typeFilter) return false;
      if (q && !item.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, typeFilter, search]);

  const playlistOrientationCounts = useMemo(() => {
    const landscape = playlists.filter((p) => p.orientation === 'Landscape').length;
    const portrait = playlists.filter((p) => p.orientation === 'Portrait').length;
    return { all: playlists.length, Landscape: landscape, Portrait: portrait } as Record<PlaylistOrientationFilter, number>;
  }, [playlists]);

  const filteredPlaylists = useMemo(() => {
    const q = playlistSearch.trim().toLowerCase();
    return playlists.filter((p) => {
      if (playlistOrientationFilter !== 'all' && p.orientation !== playlistOrientationFilter) return false;
      if (q && !p.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [playlists, playlistSearch, playlistOrientationFilter]);

  const hasFilters = Boolean(search.trim()) || typeFilter !== 'all';

  const previewPlaylistItems: PreviewMediaItem[] = useMemo(() => {
    if (!previewPlaylist) return [];
    return [...previewPlaylist.mediaEntries]
      .sort((a, b) => a.sequenceNo - b.sequenceNo)
      .map((entry) => items.find((m) => m.id === entry.mediaId))
      .filter((m): m is MediaLibraryItem => Boolean(m))
      .map((m) => ({
        id: m.id,
        mediaId: m.id,
        name: m.name,
        url: m.url,
        mimeType: m.mimeType,
        orientation: m.orientation,
      }));
  }, [previewPlaylist, items]);

  const handleFilesSelected = (files: File[]) => {
    if (!currentLocationId) {
      addToast('No outlet selected — pick a location first.', 'error');
      return;
    }
    files.forEach((file) => {
      const uploadId = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setUploadingFiles((prev) => [...prev, { id: uploadId, name: file.name, progress: 0 }]);

      const onProgress = (percent: number) => {
        setUploadingFiles((prev) => prev.map((f) => (f.id === uploadId ? { ...f, progress: percent } : f)));
      };

      uploadMedia(currentLocationId, file, uploadOrientation, onProgress)
        .then(() => addToast(`${file.name} uploaded`, 'success'))
        .catch((err: Error) => addToast(err.message || `Failed to upload ${file.name}`, 'error'))
        .finally(() => setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadId)));
    });
  };

  const handleFilesRejected = (files: File[]) => {
    files.forEach((file) => addToast(`${file.name} exceeds the 50MB upload limit`, 'error'));
  };

  const handleDelete = (item: MediaLibraryItem) => {
    if (!currentLocationId) {
      addToast('No outlet selected — pick a location first.', 'error');
      return;
    }
    deleteMedia(item.id, currentLocationId)
      .then(() => addToast(`${item.name} deleted`, 'info'))
      .catch((err: Error) => addToast(err.message || `Failed to delete ${item.name}`, 'error'));
  };

  const handleDeletePlaylist = (playlist: Playlist) => {
    if (!currentLocationId) {
      addToast('No outlet selected — pick a location first.', 'error');
      return;
    }
    deletePlaylist(playlist.id, currentLocationId)
      .then(() => addToast(`${playlist.name} deleted`, 'info'))
      .catch((err: Error) => addToast(err.message || `Failed to delete ${playlist.name}`, 'error'));
  };

  const uploadTabContent = (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <StatCard icon="collections" label="Total Media" value={counts.all} />
        <StatCard icon="movie" label="Video" value={counts.video} />
        <StatCard icon="photo_library" label="Images" value={counts.image} />
        <StatCard icon="inventory_2" label="Available Slots" value={`${remainingSlots} / ${MEDIA_LIBRARY_SLOT_LIMIT}`} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: T2, fontFamily: FONT }}>Tag new uploads as</span>
          <OrientationToggle value={uploadOrientation} onChange={setUploadOrientation} />
        </div>
        <div style={{ fontSize: 11, color: T3, fontFamily: FONT }}>
          <strong style={{ fontWeight: 700, color: T2 }}>Note:</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
            <li>Set orientation before uploading — it tags the files you add next.</li>
            <li>When uploading in bulk, all selected files get tagged with the same orientation — files with a different actual orientation may end up mistagged.</li>
          </ul>
        </div>
      </div>

      <MediaUploadArea remainingSlots={remainingSlots} onFilesSelected={handleFilesSelected} onFilesRejected={handleFilesRejected} />

      {uploadingFiles.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {uploadingFiles.map((f) => (
            <div key={f.id} style={{ background: 'var(--surface, #fff)', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: T1, fontFamily: FONT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.name}
                </span>
                <span style={{ fontSize: 11, color: T3, fontFamily: FONT, flexShrink: 0 }}>{f.progress}%</span>
              </div>
              <div style={{ height: 6, borderRadius: 999, background: 'var(--hover, #F0F1F5)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${f.progress}%`, borderRadius: 999, background: ACCENT, transition: 'width .2s ease' }} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', minWidth: 200 }}>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by media name..." />
        </div>
        <TypeFilterTabs value={typeFilter} onChange={setTypeFilter} counts={counts} />
      </div>

      {filtered.length === 0 ? (
        <div style={{ background: 'var(--surface, #fff)', border: `1px solid ${BORDER}`, borderRadius: 14 }}>
          <MediaLibraryEmptyState hasFilters={hasFilters} onAction={() => { setSearch(''); setTypeFilter('all'); }} showAction={false} />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {filtered.map((item) => (
            <MediaCard
              key={item.id}
              item={item}
              onPreview={() => setPreviewItem(item)}
              onDelete={() => handleDelete(item)}
            />
          ))}
        </div>
      )}
    </>
  );

  const playlistsTabContent = (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <StatCard icon="playlist_play" label={`Playlist${playlists.length === 1 ? '' : 's'}`} value={playlists.length} />
        <button
          type="button"
          onClick={() => navigate(displayPaths.playlistCreate)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 10, border: 'none',
            background: ACCENT, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT,
            boxShadow: '0 2px 8px rgba(210,58,59,0.20)', transition: 'all .15s ease', marginLeft: 'auto',
          }}
        >
          <Icon name="add" size={18} color="#fff" />
          Create Playlist
        </button>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', minWidth: 200 }}>
          <SearchInput value={playlistSearch} onChange={setPlaylistSearch} placeholder="Search by playlist name..." />
        </div>
        <PlaylistOrientationFilterTabs value={playlistOrientationFilter} onChange={setPlaylistOrientationFilter} counts={playlistOrientationCounts} />
      </div>

      {filteredPlaylists.length === 0 ? (
        <div style={{ background: 'var(--surface, #fff)', border: `1px solid ${BORDER}`, borderRadius: 14 }}>
          <MediaLibraryEmptyState
            hasFilters={Boolean(playlistSearch.trim()) || playlistOrientationFilter !== 'all'}
            onAction={() => navigate(displayPaths.playlistCreate)}
            icon="playlist_play"
            title="No playlists yet"
            subtitle="Group media from your library into a schedulable playlist"
            actionIcon="add"
            actionLabel="Create Playlist"
          />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {filteredPlaylists.map((playlist) => (
            <PlaylistCard
              key={playlist.id}
              playlist={playlist}
              mediaItems={items}
              mediaCountLabel={`${playlist.mediaEntries.length} media item${playlist.mediaEntries.length === 1 ? '' : 's'}`}
              onPreview={() => setPreviewPlaylist(playlist)}
              onEdit={() => navigate(`${displayPaths.playlistCreate}?id=${playlist.id}`)}
              onDelete={() => handleDeletePlaylist(playlist)}
            />
          ))}
        </div>
      )}
    </>
  );

  const content = (
    <div style={{ padding: isMobile ? '12px 16px 88px' : 28, display: 'flex', flexDirection: 'column', gap: isMobile ? 14 : 20, width: '100%', boxSizing: 'border-box' }}>
      {!isMobile && (
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: T1, fontFamily: FONT }}>Media Library</div>
          <div style={{ fontSize: 13, color: T3, marginTop: 2, fontFamily: FONT }}>Upload assets and build playlists used across your displays.</div>
        </div>
      )}

      <TabBar value={tab} onChange={setTab} />

      {tab === 'upload' ? uploadTabContent : playlistsTabContent}

      <MediaCarouselPreviewModal
        open={!!previewItem}
        title={previewItem?.name}
        items={previewItem ? [{ id: previewItem.id, mediaId: previewItem.id, name: previewItem.name, url: previewItem.url, mimeType: previewItem.mimeType, orientation: previewItem.orientation }] : []}
        onClose={() => setPreviewItem(null)}
      />

      <MediaCarouselPreviewModal
        open={!!previewPlaylist}
        title={previewPlaylist?.name}
        items={previewPlaylistItems}
        onClose={() => setPreviewPlaylist(null)}
      />

      <DisplayToastContainer />
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ background: 'var(--bg, #F5F6FA)', minHeight: '100vh', fontFamily: FONT, overflowX: 'hidden', width: '100%', boxSizing: 'border-box' }}>
        {content}
        <BottomNav />
      </div>
    );
  }

  return (
    <DesktopLayout>
      <div style={{ width: '100%', minWidth: 0, background: 'var(--bg, #F5F6FA)', boxSizing: 'border-box', fontFamily: FONT }}>
        {content}
      </div>
    </DesktopLayout>
  );
}
